// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import Formatter from '../../i18n/formatter';
import { __, getCurrency } from '../../i18n/l10n';
import BetslipOptionRepeatBetButtonContainer from '../../modules/betslip-options/components/BetslipOptionRepeatBetButton/BetslipOptionRepeatBetButtonContainer';
import BoomHistoryBetslipContainer from '../../modules/boom/components/BoomHistoryBetslip/BoomHistoryBetslipContainer';
import GRHistoryBetslipEligibleHeadBandContainer from '../../modules/gift-race/containers/GRHistoryBetslipEligibleHeadBandContainer';
import KLHistoryBetslipEligibleCollectibleContainer from '../../modules/koh-lanta/containers/KLHistoryBetslipEligibleCollectibleContainer';
import HistoryBetslipMissionEligibleTagContainer from '../../modules/missions/components/HistoryBetslipMissionEligibleTag/HistoryBetslipMissionEligibleTagContainer';
import MPHistoryBetslipContainer from '../../modules/monopoly/containers/MPHistoryBetslipContainer';
import { BetslipState } from '../../states/entities/betslips';
import theme from '../../styled-components/dark';
import { AutoScrollAnchor } from '../../styled-components/shared';
import { BetslipStatuses } from '../../types/betslip';
import { Licences } from '../../types/common';
import type { Licence } from '../../types/common';
import { BetslipTypes, StatusFilters, type StatusFilter } from '../../types/history';
import getSystemTitle from '../../utils/getSystemTitle';
import CollapseListItem from '../CollapseListItem';
import FiltersWrapper, { FilterWrapperListClassName } from '../FiltersWrapper';
import HistoryItemBetslipFooter from '../HistoryItemBetslipFooter';
import HistoryItemBetslipHeaderContainer from '../HistoryItemBetslipHeader/HistoryItemBetslipHeaderContainer';
import HistoryItemBetslipSummary from '../HistoryItemBetslipSummary';
import HistoryItemCashoutButtonsContainer from '../HistoryItemCashoutButtons/HistoryItemCashoutButtonsContainer';
import HistoryItemOutcomeCardContainer from '../HistoryItemOutcomeCard/HistoryItemOutcomeCardContainer';
import OutcomesSummaryContainer from '../HistoryItemOutcomesSummary/HistoryItemOutcomesSummaryContainer';
import HistoryItemSystemCombination from '../HistoryItemSystemCombination';
import InfosLabel, { InfosLabelTypes } from '../InfosLabel/InfosLabel';

import { HistoryOutcomeDetails } from './HistoryItem.selector';

const BetslipWrapper = styled.div`
  position: relative;
  line-height: normal;
  padding: 0 0.5em;
  margin-bottom: 1em;
  font-family: 'BettingArchivoNarrow';

  ${!Target.__MOBILE__
    ? css`
        font-size: 1.2em;
      `
    : ''}
`;

const NoTextTransform = styled.span`
  text-transform: none;
`;

const OutcomesWrapper = styled.div`
  padding-bottom: 0;
`;

const HeadbandsWrapper = styled.div`
  max-height: 1.8em;
  overflow: visible;

  > * {
    margin: 1em;
    margin-bottom: 0;
  }
`;

const ContentWrapper = styled.div`
  border-radius: ${theme.Card.borderRadius};
  background-color: ${theme.History.betslip.backgroundColor};
  overflow: hidden;
`;

const SystemCombinationWrapper = styled(FiltersWrapper)`
  width: 100%;
  margin: 0.7em 0;
  padding: 0 1em;
  background: ${theme.History.betslip.backgroundColor};
  --BScrollViewLeftShadow-backgroundColor: ${theme.History.betslip.backgroundColor};
  --BScrollViewRightShadow-backgroundColor: ${theme.History.betslip.backgroundColor};

  & .${FilterWrapperListClassName} {
    gap: 0.33em;
  }
`;

const StyledBetslipOptionRepeatBetButtonContainer = styled(BetslipOptionRepeatBetButtonContainer)`
  margin: 0.3em 1em 1em;
`;

type HistoryItemPropsType = {
  id: number;
  betslip?: BetslipState;
  cashoutStatus?: boolean | null;
  isCashoutEnabled: boolean;
  licence: Licence;
  cashout: unknown;
  showPendingCashoutInfosLabel: boolean;
  isCompact: boolean;
  outcomes?: Array<HistoryOutcomeDetails>;
  currentPage?: number;
  setPreviousPosition?: (pageNumber: number, anchorId: string) => void;
  anchorToOpen?: string;
  isCashoutHidden?: boolean;
  className?: string;
};

const HistoryItem = ({
  id,
  betslip,
  cashoutStatus,
  isCashoutEnabled,
  licence,
  cashout,
  showPendingCashoutInfosLabel,
  isCompact,
  outcomes,
  currentPage,
  setPreviousPosition,
  anchorToOpen,
  className,
  isCashoutHidden = false,
}: HistoryItemPropsType) => {
  const {
    aamsTicketId,
    betslipReference,
    validationDate,
    type,
    status,
    winningsAmount,
    winningsBonus,
    possibleWinnings,
    possibleBonus,
    globalOdds,
    details = [],
    betAmount,
    freebet,
    bankers = [],
    cnp = [],
    cashoutAvailable,
    cashoutValue,
    promotionTitle = '',
    cashoutAllowed,
    completeBetAllowed,
    voidReason,
  } = betslip ?? ({} as BetslipState);
  const hasMultipleOutcome = useMemo(() => (outcomes?.length ?? 0) > 1, [outcomes]);

  const reference = useMemo(() => {
    let result = betslipReference?.replace(/^(\w)+-/, '') ?? false;

    if (!result) {
      return null;
    }

    if (licence === Licences.ES) {
      result += ` / ${id}`;
    }
    return result;
  }, [betslipReference, licence, id]);

  const anchorId = useMemo(() => `history-betslip-${betslipReference}`, [betslipReference]);
  const hasOpenAnchor = useRef(false);

  const [statusFilter, setStatusFilter] = useState(isCompact && anchorToOpen !== anchorId ? null : StatusFilters.ALL);

  const toggleOutcomes = useCallback(
    (selectedStatus: StatusFilter) => {
      if (selectedStatus !== StatusFilters.ALL && selectedStatus === statusFilter) {
        setStatusFilter(StatusFilters.ALL);
      } else if (selectedStatus === StatusFilters.ALL && statusFilter !== null) {
        setStatusFilter(null);
      } else {
        setStatusFilter(selectedStatus);
      }
    },
    [statusFilter],
  );

  useEffect(() => {
    if (hasOpenAnchor.current) {
      setStatusFilter(isCompact ? null : StatusFilters.ALL);
    } else {
      setStatusFilter(isCompact && anchorToOpen !== anchorId ? null : StatusFilters.ALL);
      hasOpenAnchor.current = true;
    }
  }, [isCompact, anchorToOpen, anchorId]);

  const aamsTicket = useMemo(
    () => (aamsTicketId ? <NoTextTransform> / {aamsTicketId}</NoTextTransform> : null),
    [aamsTicketId],
  );

  const shouldHideOutcomes = useMemo(() => !statusFilter && hasMultipleOutcome, [statusFilter, hasMultipleOutcome]);

  const systemCombinations = useMemo(() => {
    if (type === BetslipTypes.SYSTEM) {
      return (
        <SystemCombinationWrapper noAutoScroll>
          {cnp.map((x, idx) => (
            <HistoryItemSystemCombination
              key={`system-combi-${idx}`}
              label={getSystemTitle(details.length, bankers.length, x)}
            />
          ))}
        </SystemCombinationWrapper>
      );
    }
    return null;
  }, [type, details.length, bankers.length, cnp]);

  const onClickOutcomes = useCallback(() => {
    setPreviousPosition?.(currentPage, anchorId);
  }, [setPreviousPosition, currentPage, anchorId]);

  return (
    <BetslipWrapper className={className} data-testid={`history-item-${id}`}>
      <AutoScrollAnchor data-anchorid={anchorId} />

      <HistoryItemBetslipHeaderContainer
        betslipStatus={status}
        betslipType={type}
        betslipId={id}
        winnings={winningsAmount}
        outcomes={outcomes}
        isSimpleBet={!hasMultipleOutcome}
      />

      <ContentWrapper>
        <HeadbandsWrapper>
          <GRHistoryBetslipEligibleHeadBandContainer betslipId={id} isCompact dark />
          <KLHistoryBetslipEligibleCollectibleContainer betslipId={id} isCompact dark />
          <HistoryBetslipMissionEligibleTagContainer betslipId={id} isCompact dark />
          <MPHistoryBetslipContainer betslipId={id} />
          <BoomHistoryBetslipContainer betslipId={id} />
        </HeadbandsWrapper>
        {hasMultipleOutcome ? (
          <OutcomesSummaryContainer
            outcomes={outcomes}
            isExpand={!shouldHideOutcomes}
            statusFilter={statusFilter}
            onClickExpand={toggleOutcomes}
            outcomeNb={outcomes.length}
          />
        ) : null}
        <CollapseListItem isOpened={!shouldHideOutcomes}>
          <OutcomesWrapper onClick={onClickOutcomes}>
            {outcomes
              .sort((outcome1, outcome2) => outcome2.isLive - outcome1.isLive)
              .map((outcomeInfo, index) => {
                const { outcomeId, odds, initialOdds } = outcomeInfo;
                const isBanker = bankers.indexOf(outcomeId) !== -1;
                return (
                  <HistoryItemOutcomeCardContainer
                    key={`history-outcome-card-${outcomeId}-${index}`}
                    isBanker={isBanker}
                    odds={odds}
                    initialOdds={initialOdds}
                    id={outcomeId}
                    betslipStatus={status}
                    betslipVoidReason={voidReason}
                    statusFilter={statusFilter}
                    isSimpleBet={!hasMultipleOutcome}
                    isUserPrefsCompact={isCompact}
                    outcomeInfo={outcomeInfo}
                  />
                );
              })}
          </OutcomesWrapper>
          {systemCombinations}
          {hasMultipleOutcome ? (
            <HistoryItemBetslipSummary
              type={type}
              globalOdds={globalOdds}
              details={details}
              freebet={freebet}
              betAmount={betAmount}
              status={status}
              winningsAmount={winningsAmount}
              winningsBonus={winningsBonus}
              promotionTitle={promotionTitle}
              possibleBonus={possibleBonus}
              possibleWinnings={possibleWinnings}
            />
          ) : null}
        </CollapseListItem>
        {shouldHideOutcomes || !hasMultipleOutcome ? (
          <HistoryItemBetslipSummary
            isCompact
            type={type}
            globalOdds={globalOdds}
            details={details}
            freebet={freebet}
            betAmount={betAmount}
            status={status}
            winningsAmount={winningsAmount}
            winningsBonus={winningsBonus}
            promotionTitle={promotionTitle}
            possibleBonus={possibleBonus}
            possibleWinnings={possibleWinnings}
          />
        ) : null}
        {status !== BetslipStatuses.RUNNING ? (
          <StyledBetslipOptionRepeatBetButtonContainer betslipReference={betslipReference} />
        ) : null}
        {isCashoutEnabled && !isCashoutHidden ? (
          <HistoryItemCashoutButtonsContainer
            cashoutAllowed={cashoutAllowed}
            completeBetAllowed={completeBetAllowed}
            cashoutValue={cashoutValue}
            cashoutStatus={cashoutStatus}
            cashoutAvailable={cashoutAvailable}
            licence={licence}
            id={id}
            cashout={cashout}
          />
        ) : null}
        {showPendingCashoutInfosLabel ? (
          <InfosLabel
            symbol={'!'}
            type={InfosLabelTypes.INFOS_YELLOW}
            label={__(
              'Your winnings of %1$s will be credited to you as soon as all bets are settled.',
              Formatter.formatNumber(winningsAmount + (winningsBonus || 0), getCurrency()),
            )}
          />
        ) : null}
        {!shouldHideOutcomes ? (
          <HistoryItemBetslipFooter date={validationDate} betslipReference={[reference, aamsTicket]} />
        ) : null}
      </ContentWrapper>
    </BetslipWrapper>
  );
};

// __('Your winnings of %1$s will be credited to you as soon as all bets are settled.')

export default HistoryItem;
